import React from 'react';
import type { GroupProps } from '@chakra-ui/react';
import {
  Box,
  Field,
  Group,
  Input as InputChakra,
  InputAddon,
  InputProps,
  NativeSelect,
  Spinner,
  Stack,
  Textarea,
} from '@chakra-ui/react';
import { ReactComponent as InputErrorIcon } from 'assets/icons/input_error.svg';
import { colors } from 'theme';

type InputPropTypes = {
  error?: string;
  invalid?: boolean;
  isRequired?: boolean;
  leftValue?: string | React.ReactElement;
  rightValue?: string | React.ReactElement;
  label: string;
  isSelectBox?: boolean;
  isTextArea?: boolean;
  isEmptyAtFirst?: boolean;
  options?: { flag?: any; label: string; value: string; disabled?: boolean }[];
  inputGroupStyle?: GroupProps;
  labelStyle?: React.ComponentProps<typeof Field.Label>;
  isLoading?: boolean;
  withIcon?: boolean;
} & InputProps;

const Input = React.forwardRef<HTMLInputElement, InputPropTypes>(
  (
    {
      invalid,
      isRequired = false,
      leftValue = '',
      rightValue = '',
      label,
      error,
      isLoading = false,
      isSelectBox = false,
      isTextArea = false,
      isEmptyAtFirst = false,
      options = [],
      inputGroupStyle,
      labelStyle,
      withIcon: _unusedWithIcon = true,
      type,
      onWheel: onWheelProp,
      ...rest
    },
    forwardedRef
  ) => {
    return (
      <Field.Root invalid={invalid} required={isRequired}>
        <Field.Label
          fontWeight="medium"
          color={error ? 'error' : 'secondary2'}
          {...labelStyle}
        >
          {label}
        </Field.Label>
        <Group attached w="full" mb={4} {...inputGroupStyle}>
          {Boolean(leftValue) && (
            <InputAddon borderColor={error ? 'error' : 'body2'} height="12">
              {leftValue}
            </InputAddon>
          )}
          {Boolean(isLoading) && (
            <Stack
              pos="absolute"
              right={4}
              top="50%"
              transform="translate(0, -50%)"
            >
              <Spinner color="primary" />
            </Stack>
          )}
          {isSelectBox ? (
            <NativeSelect.Root>
              <NativeSelect.Field
                height="12"
                border="1px solid"
                borderColor={error ? 'error' : 'body2'}
                borderRadius={!leftValue && !rightValue ? 'lg' : undefined}
                borderRightRadius={leftValue ? 'lg' : undefined}
                borderLeftRadius={rightValue ? 'lg' : undefined}
                background={error ? 'lightError' : 'dark'}
                color={error ? 'error' : 'body'}
                fontSize="md"
                _placeholder={{ color: error ? 'error' : 'body2' }}
                _hover={{
                  bg: error ? 'lightError' : 'rgba(241, 248, 242, 0.1)',
                }}
                _focus={{
                  _placeholder: { color: 'transparent' },
                  background: error ? 'lightBg' : 'dark',
                  borderColor: error ? 'error' : 'primary',
                }}
                css={{
                  colorScheme: 'dark',
                  '& .chakra-select__icon-wrapper': {
                    right: '10px !important',
                  },
                  '& option': {
                    backgroundColor: colors.dark,
                    color: colors.body,
                  },
                }}
                ref={forwardedRef}
                disabled={isLoading}
                {...(rest as any)}
              >
                {isEmptyAtFirst && <option disabled selected></option>}
                {options.map((item) => {
                  return (
                    <option
                      key={item.value}
                      disabled={item.disabled || false}
                      value={item.value}
                    >
                      {item.label}
                    </option>
                  );
                })}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          ) : isTextArea ? (
            <Textarea
              height="12"
              border="1px solid"
              borderColor={error ? 'error' : 'body2'}
              borderRadius="lg"
              background={error ? 'lightError' : 'dark'}
              color={error ? 'error' : 'body'}
              fontSize="md"
              _placeholder={{ color: error ? 'error' : 'body2' }}
              _hover={{
                bg: error ? 'lightError' : 'rgba(241, 248, 242, 0.1)',
              }}
              _focus={{
                _placeholder: { color: 'transparent' },
                background: error ? 'lightBg' : 'dark',
                borderColor: error ? 'error' : 'primary',
              }}
              ref={forwardedRef}
              {...(rest as any)}
            />
          ) : (
            <InputChakra
              height="12"
              border="1px solid"
              borderColor={error ? 'error' : 'body2'}
              borderRadius="lg"
              background={error ? 'lightError' : 'dark'}
              color={error ? 'error' : 'body'}
              fontSize="md"
              _placeholder={{ color: error ? 'error' : 'body2' }}
              _hover={{
                bg: error ? 'lightError' : 'rgba(241, 248, 242, 0.1)',
              }}
              _focus={{
                _placeholder: { color: 'transparent' },
                background: error ? 'lightBg' : 'dark',
                borderColor: error ? 'error' : 'primary',
              }}
              ref={forwardedRef}
              type={type}
              onWheel={(e: React.WheelEvent<HTMLInputElement>) => {
                onWheelProp?.(e);
                if (type === 'number') {
                  e.currentTarget.blur();
                }
              }}
              {...rest}
            />
          )}
          {Boolean(rightValue) && (
            <InputAddon borderColor={error ? 'error' : 'body2'} height="12">
              {rightValue}
            </InputAddon>
          )}
        </Group>
        {error ? (
          <Box
            color="error"
            fontSize="sm"
            textAlign="start"
            display="flex"
            alignItems="center"
            gap={1}
            pos="absolute"
            left={0}
            bottom={-5}
          >
            <InputErrorIcon />
            {error}
          </Box>
        ) : (
          ''
        )}
      </Field.Root>
    );
  }
);

Input.displayName = 'Input';
export default Input;
